// The series form, shared by create and edit.
//
// Two things this form has to teach the organizer, because a lazy materialization
// model is not self-evident:
//
//   1. Creating a series creates NO events yet. The upcoming-dates preview on the
//      detail page is what makes the rule concrete.
//   2. "Materialize this many days before" plus a time IS the schedule. Leaving
//      the time blank means the series never generates on its own — occurrences
//      come only from the manual button. That is a real, useful mode, but it is
//      silent, so the form says so out loud.
//
// The recurrence fields themselves come from `recurrenceFields()`, shared with the
// create-event form's "Repeats" branch so the two can never disagree about what a
// valid rule is. The event-type copy overrides are rendered from the selected
// type, the same way the event form renders from the type registry.

import {
  EVENT_TYPE_KEYS,
  EVENT_TYPES,
  eventType,
  eventTypeUsesEndDate,
  hookCanFire,
  type EventTypeKey,
  type LifecycleHook,
  type SeriesDetail,
  type SeriesRequest,
} from '@msc/shared';
import { el } from '../dom.js';
import { messageCopyFields, type MessageCopyFields } from './messageCopyFields.js';
import { placeFields } from './placeFields.js';

/** Shown under both title boxes, so the token is discoverable where it is used. */
const TITLE_HINT = 'Tip: put {day} in the title and each event gets its own weekday. "{day} Night Poker" becomes "Tuesday Night Poker".';
import { recurrenceFields } from './recurrenceFields.js';
import { tzPicker } from './tzPicker.js';

export interface SeriesFormOptions {
  /** Present when editing. */
  series?: SeriesDetail;
  groupTimeZone: string | null;
  submitLabel: string;
  action: string;
  onSubmit: (body: SeriesRequest) => Promise<void>;
}

export function seriesForm(opts: SeriesFormOptions): HTMLElement {
  const existing = opts.series;

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, opts.submitLabel);

  const title = input({ id: 'sr-title', type: 'text', required: true, maxlength: 191, value: existing?.title ?? '' });
  const startDate = input({ id: 'sr-start-date', type: 'date', required: true, value: existing?.startDate ?? '' });
  const startTime = input({ id: 'sr-start', type: 'time', value: hhmm(existing?.startTime) });
  const endTime = input({ id: 'sr-end', type: 'time', value: hhmm(existing?.endTime) });
  const location = input({ id: 'sr-location', type: 'text', maxlength: 255, value: existing?.location ?? '' });
  const place = placeFields('sr', existing);
  const rsvpMin = input({
    id: 'sr-min',
    type: 'number',
    min: 1,
    placeholder: 'No minimum',
    value: existing?.rsvpMin == null ? '' : String(existing.rsvpMin),
  });
  const rsvpMax = input({
    id: 'sr-max',
    type: 'number',
    min: 1,
    placeholder: 'No limit',
    value: existing?.rsvpMax == null ? '' : String(existing.rsvpMax),
  });

  // The searchable picker, matching the event form and the two group forms. This
  // was a bare text input until 2026-08-21 — the last form the picker restoration
  // missed — which meant the zone had to be typed as an exact IANA id, with none
  // of the "mountain" / "MST" / "denver" aliases the curated list carries.
  //
  // Blank means "inherit the group's zone", stored as NULL, so only an explicit
  // zone is echoed back: showing the inherited one as a value would silently PIN
  // it, and a later change to the group would stop reaching this series. Which is
  // also why `autodetect` is absent — filling an empty picker from the browser
  // would pin a zone onto every series nobody chose one for.
  //
  // The inherit row names the GROUP zone, not the site zone. Two different
  // chains; see shared/src/time.ts.
  const timeZone = tzPicker({
    name: 'timeZone',
    value: existing?.timeZoneIsExplicit ? existing.timeZone : '',
    inheritLabel: 'Group default',
    inheritHint: opts.groupTimeZone ?? '',
    id: 'sr-tz',
  });

  const description = textarea('sr-description', 4, existing?.description ?? '');
  const feedMessage = textarea('sr-feed-message', 2, existing?.feedMessage ?? '');

  // --- The recurrence rule ------------------------------------------------
  //
  // `allowNone` is false here: a series that does not repeat is not a series.
  // The event form is the one that offers the off switch.
  const recurrence = recurrenceFields({
    idPrefix: 'sr',
    allowNone: false,
    existing: {
      recurrenceType: existing?.recurrenceType ?? null,
      recurrenceDays: existing?.recurrenceDays ?? null,
      recurrenceInterval: existing?.recurrenceInterval ?? null,
      endDate: existing?.endDate ?? null,
      feedPostDaysBefore: existing?.feedPostDaysBefore ?? null,
      feedPostTime: existing?.feedPostTime ?? null,
    },
  });

  // --- Event type and its copy overrides ----------------------------------

  const typeSelect = el('select', { id: 'sr-type' }) as HTMLSelectElement;
  for (const key of EVENT_TYPE_KEYS) {
    const def = EVENT_TYPES[key];
    // A multi-day type cannot recur, so it is absent from the picker entirely
    // rather than offered and then rejected. The server rejects it too.
    if (eventTypeUsesEndDate(key)) continue;
    const opt = el('option', { value: key }, `${def.icon} ${def.label}`) as HTMLOptionElement;
    if (key === (existing?.eventType ?? 'generic')) opt.selected = true;
    typeSelect.append(opt);
  }

  const typeBlock = el('div', { class: 'type-block' });
  let copy: MessageCopyFields | null = null;

  function renderTypeBlock(): void {
    const def = eventType(typeSelect.value as EventTypeKey);
    const children: HTMLElement[] = [];

    const hooks = Object.entries(def.hooks) as [LifecycleHook, string][];
    copy = null;
    if (hooks.length > 0) {
      copy = messageCopyFields({
        hooks,
        existing: existing?.typeMessages,
        idPrefix: 'sr',
        intro: 'Inherited by every occurrence.',
      });
      children.push(copy.element);
      syncReachableHooks();
    }
    typeBlock.replaceChildren(...children);
  }

  /**
   * Same rule as the event form: a message box only appears when its hook can
   * fire. `min_met` needs a minimum, `full` needs a maximum, and the thresholds
   * here are inherited by every occurrence — so a box with no threshold behind
   * it is dead configuration on every event the series ever creates, not just
   * one.
   */
  function syncReachableHooks(): void {
    const thresholds = {
      rsvpMin: numberOrNull(rsvpMin.value),
      rsvpMax: numberOrNull(rsvpMax.value),
    };
    copy?.setReachable((hook) => hookCanFire(typeSelect.value, hook, thresholds));
  }

  rsvpMin.addEventListener('input', syncReachableHooks);
  rsvpMax.addEventListener('input', syncReachableHooks);

  typeSelect.addEventListener('change', renderTypeBlock);

  renderTypeBlock();

  const form = el('form', { method: 'post', action: opts.action, novalidate: true }, [
    el('label', { for: 'sr-title' }, 'Title'),
    el('p', { class: 'muted small' }, TITLE_HINT),
    title,
    el('label', { for: 'sr-type' }, 'Event type'),
    typeSelect,
    recurrence.typeRow,
    recurrence.weekdayRow,
    recurrence.intervalRow,
    el('label', { for: 'sr-start-date' }, 'First occurrence'),
    startDate,
    recurrence.untilRow,
    el('label', { for: 'sr-start' }, 'Start time'),
    startTime,
    el('label', { for: 'sr-end' }, 'End time'),
    endTime,
    el('label', { for: 'sr-location' }, 'Location'),
    location,
    place.element,
    el('label', { for: 'sr-min' }, 'Minimum RSVPs'),
    rsvpMin,
    el('label', { for: 'sr-max' }, 'Maximum RSVPs'),
    rsvpMax,
    el('label', { for: 'sr-tz' }, 'Timezone'),
    timeZone.root,
    el(
      'p',
      { class: 'muted small' },
      'Inherited by every occurrence. Blank inherits the group’s zone.',
    ),

    recurrence.scheduleBlock,

    el('label', { for: 'sr-description' }, 'Details'),
    description,
    el('label', { for: 'sr-feed-message' }, 'Note to members'),
    feedMessage,
    typeBlock,
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');

    // {} when the selected type has no hooks, which is the correct payload.
    const typeMessages: Record<string, string> = copy?.value() ?? {};

    const rule = recurrence.value();
    // Unreachable: `allowNone` is false, so there is no "does not repeat" option
    // to select. Narrowing rather than asserting keeps it that way.
    if (!rule.repeats) {
      error.textContent = 'Pick how often this repeats.';
      error.hidden = false;
      submit.removeAttribute('disabled');
      return;
    }

    const body: SeriesRequest = {
      title: title.value.trim(),
      eventType: typeSelect.value,
      recurrenceType: rule.recurrenceType,
      recurrenceDays: rule.recurrenceDays,
      recurrenceInterval: rule.recurrenceInterval,
      startDate: startDate.value,
      endDate: rule.endDate,
      startTime: startTime.value || null,
      endTime: endTime.value || null,
      location: location.value.trim(),
      ...place.value(),
      rsvpMin: numberOrNull(rsvpMin.value),
      rsvpMax: numberOrNull(rsvpMax.value),
      feedPostDaysBefore: rule.feedPostDaysBefore,
      feedPostTime: rule.feedPostTime,
      timeZone: timeZone.value(),
      description: description.value.trim(),
      feedMessage: feedMessage.value.trim(),
      typeMessages,
    };

    try {
      await opts.onSubmit(body);
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  return form;
}

/** A TIME column round-trips as HH:MM:SS; <input type="time"> wants HH:MM. */
function hhmm(time: string | null | undefined): string {
  return time ? time.slice(0, 5) : '';
}

function numberOrNull(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function input(attrs: Record<string, string | number | boolean>): HTMLInputElement {
  const node = el('input', attrs) as HTMLInputElement;
  // Also set the property: `value` as an attribute is only the DEFAULT value, so
  // a re-render after a failed submit would otherwise show stale text.
  if (typeof attrs.value === 'string') node.value = attrs.value;
  return node;
}

function textarea(id: string, rows: number, value: string): HTMLTextAreaElement {
  const node = el('textarea', { id, rows }) as HTMLTextAreaElement;
  node.value = value;
  return node;
}
